/** Email-first sign-in with an optional passkey and a password fallback. */
import React from "react";
import "./SLTLoginPage.css";
import {withRouter} from "react-router-dom";
import FormMessage from './FormMessage.js';
import SLTSnowAppErrHelper from "./SLTSnowAppErrHelper.js";

class SLTLoginPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            email: '', password: '', step: 'email', hasPasskeys: false,
            isPasskeyPossible: false, discovering: false, pending: null, error: null, message: null,
        };
        this.emailInput = React.createRef();
        this.componentIsMounted = false;
        this.attempt = 0;
        this.busy = false;
        this.cancelLogin = null;
        this.cancelAutofill = null;
        this.autofillGeneration = 0;
    }

    componentDidMount() {
        this.componentIsMounted = true;
        if (this.props.isLoggedIn) {
            this.props.history.replace('/directory');
            return;
        }
        // WebAuthn support allows platform, security-key, and cross-device options.
        // It does not establish whether this device holds an account's passkeys.
        this.setState({isPasskeyPossible: !!window.PublicKeyCredential}, () => this.startAutofill());
    }

    componentWillUnmount() {
        this.componentIsMounted = false;
        this.cancelAttempt();
    }

    stopAutofill() {
        this.autofillGeneration += 1;
        if (typeof this.cancelAutofill === 'function') this.cancelAutofill();
        this.cancelAutofill = null;
    }

    async startAutofill() {
        const credentialApi = window.PublicKeyCredential;
        if (!this.props.onDiscoverableLogin || !credentialApi ||
            typeof credentialApi.isConditionalMediationAvailable !== 'function') return;
        const generation = this.autofillGeneration;
        try {
            if (!await credentialApi.isConditionalMediationAvailable() || !this.componentIsMounted ||
                generation !== this.autofillGeneration || this.busy || this.state.step !== 'email') return;
            this.cancelAutofill = this.props.onDiscoverableLogin(() => {
                if (this.componentIsMounted && generation === this.autofillGeneration) {
                    this.props.history.replace('/directory');
                }
            }, () => {
                if (this.componentIsMounted && generation === this.autofillGeneration && this.state.pending === 'autofill') {
                    this.busy = false;
                    this.setState({pending: null, message: 'Passkey sign-in could not be completed. Try again, or use your password.'});
                }
            }, {useBrowserAutofill: true, onSelected: () => {
                if (!this.componentIsMounted || generation !== this.autofillGeneration) return;
                // A signed assertion takes priority over an outstanding account lookup.
                this.cancelAttempt(true);
                this.busy = true;
                this.setState({pending: 'autofill', step: 'passkey', discovering: true, error: null, message: null});
            }});
        } catch (error) {
            // Unsupported conditional mediation must not interrupt normal sign-in.
        }
    }

    cancelAttempt(keepAutofill = false) {
        if (!keepAutofill) this.stopAutofill();
        this.attempt += 1;
        this.busy = false;
        if (typeof this.cancelLogin === 'function') this.cancelLogin();
        this.cancelLogin = null;
    }

    changeEmail = (event) => {
        if (this.state.pending === 'autofill') return;
        this.cancelAttempt(true);
        this.setState({email: event.target.value, password: '', step: this.state.step === 'password' ? 'password' : 'email',
            hasPasskeys: false, discovering: false, pending: null, error: null, message: null});
    };

    usePassword = () => {
        this.cancelAttempt();
        this.setState({step: 'password', discovering: false, pending: null, error: null, message: null});
    };

    validEmail() {
        return this.emailInput.current && this.emailInput.current.reportValidity();
    }

    handleContinue = (event) => {
        event.preventDefault();
        if (this.busy || !this.validEmail()) return;
        this.cancelAttempt(true);
        this.busy = true;
        const attempt = ++this.attempt;
        this.setState({pending: 'lookup', error: null, message: null});
        const finish = (hasPasskeys, message = null) => {
            if (!this.componentIsMounted || attempt !== this.attempt) return;
            this.busy = false;
            this.setState({pending: null, hasPasskeys,
                step: hasPasskeys && this.state.isPasskeyPossible ? 'passkey' : 'password', message});
        };
        try {
            if (!this.props.onGetSignInMethods) throw new Error('Lookup unavailable');
            this.props.onGetSignInMethods(
                result => finish(result.hasPasskeys === true),
                () => finish(false, 'Could not check passkeys. You can still sign in with your password.'),
                this.state.email.trim());
        } catch (error) {
            finish(false, 'Could not check passkeys. You can still sign in with your password.');
        }
    };

    signIn = (usePasskey, discover = false) => {
        if (this.busy || (!discover && !this.validEmail())) return;
        if (usePasskey && !this.state.isPasskeyPossible) {
            this.setState({step: 'password', message: 'Passkeys are unavailable in this browser. Use your password to sign in.'});
            return;
        }
        if (!usePasskey && !this.state.password) return;
        this.cancelAttempt();
        this.busy = true;
        const attempt = ++this.attempt;
        this.setState({discovering: discover, pending: usePasskey ? 'passkey' : 'password', error: null, message: null,
            ...(usePasskey ? {step: 'passkey'} : {})});
        const isCurrent = () => this.componentIsMounted && attempt === this.attempt;
        const onComplete = () => {
            if (!isCurrent()) return;
            this.busy = false;
            this.setState({pending: null});
            this.props.history.replace('/directory');
        };
        const onError = (error) => {
            if (!isCurrent()) return;
            this.busy = false;
            const err = error || new Error('Sign-in failed.');
            const codes = new SLTSnowAppErrHelper().getErrorsInMessage(err.message || '');
            let message = (codes && codes.some(code => ['SLT_PROC_ERR [10000]', 'SLT_PROC_ERR [10072]'].includes(code)))
                ? 'Sign In failure, email or password not valid.' : err.message || 'Sign-in failed. Please try again.';
            if (usePasskey) message = 'Passkey sign-in was canceled or could not be completed. Try again, or use your password.';
            this.setState({pending: null, error: err, message});
        };
        try {
            if (discover && this.props.onDiscoverableLogin) {
                this.cancelLogin = this.props.onDiscoverableLogin(onComplete, onError, {useBrowserAutofill: false});
                return;
            }
            this.cancelLogin = this.props.onLogin(onComplete, onError,
                this.state.email.trim(), usePasskey ? null : this.state.password);
        } catch (error) {
            onError(error);
        }
    };

    handleSubmit = (event) => {
        event.preventDefault();
        if (this.state.step === 'email') return this.handleContinue(event);
        this.signIn(this.state.step === 'passkey', this.state.discovering);
    };

    render() {
        const {email, password, step, pending, hasPasskeys, isPasskeyPossible, discovering} = this.state;
        return (
            <div className="login-page">
                <div className="login-card">
                    <div className="login-header">
                        <div className="login-logo-circle"><span className="login-logo-letter">GB</span></div>
                        <div>
                            <h1 className="login-title">Golden Bear Reserve Directory</h1>
                            <p className="login-subtitle">Sign in to access the neighborhood directory</p>
                        </div>
                    </div>
                    <div aria-live="polite"><FormMessage message={this.state.message} error={this.state.error} /></div>
                    <form className="login-form" onSubmit={this.handleSubmit} aria-busy={!!pending}>
                        <div className="form-row">
                            <label htmlFor="email" className="form-label">Email address</label>
                            <input ref={this.emailInput} id="email" name="email" type="email"
                                autoComplete="username webauthn" required={!(step === 'passkey' && discovering)} value={email}
                                onChange={this.changeEmail} className="form-input" placeholder="you@example.com"
                                disabled={pending === 'password' || pending === 'autofill'} />
                        </div>
                        {step === 'password' && (
                            <div className="form-row">
                                <label htmlFor="password" className="form-label">Password</label>
                                <input id="password" name="password" type="password" autoComplete="current-password"
                                    required value={password} onChange={e => this.setState({password: e.target.value})}
                                    className="form-input" placeholder="Enter your password" disabled={!!pending} />
                            </div>
                        )}
                        {step === 'passkey' && <p className="login-hint">Try a passkey on this device, a security key, or another device.</p>}
                        <div className="login-form-footer">
                            <button type="submit" className="login-button" disabled={!!pending}>
                                {pending === 'lookup' ? 'Checking sign-in options…' : pending ? 'Signing in…' :
                                    step === 'email' ? 'Continue' : step === 'passkey' ? 'Continue with passkey' : 'Sign in'}
                            </button>
                            {step === 'email' && (
                                <button type="button" className="login-link-button" disabled={!!pending}
                                    onClick={() => this.signIn(true, !email.trim())}>Sign in with a passkey</button>
                            )}
                            {(step === 'passkey' || pending === 'lookup') && (
                                <button type="button" className="login-link-button" onClick={this.usePassword}>Use password instead</button>
                            )}
                            {step === 'password' && hasPasskeys && isPasskeyPossible && (
                                <button type="button" className="login-link-button" disabled={!!pending}
                                    onClick={() => this.signIn(true)}>Continue with passkey</button>
                            )}
                        </div>
                    </form>
                    <div className="login-footer"><span className="login-footer-text">
                        Need access? Contact the POA to request directory credentials.
                    </span></div>
                </div>
            </div>
        );
    }
}
export default withRouter(SLTLoginPage);
